import { supabase } from "@/integrations/supabase/client";

/**
 * Shared certification access helpers.
 *
 * Access is per certification. A learner may reach a certification scoped
 * page only when they hold a row in `user_certification_access` that is
 * not revoked and not expired. This mirrors the SQL `has_cert_access()` /
 * `has_any_cert_access()` functions so client redirects and server RLS
 * agree.
 */
export type CertAccessRow = {
  certification_id: string;
  expires_at: string | null;
  source?: string | null;
  revoked_at?: string | null;
};

/** True when the row is neither revoked nor past its expiry. */
export function isAccessRowActive(row: {
  revoked_at?: string | null;
  expires_at?: string | null;
}): boolean {
  if (row.revoked_at) return false;
  if (!row.expires_at) return true;
  const t = new Date(row.expires_at).getTime();
  return Number.isFinite(t) && t > Date.now();
}

/**
 * All non revoked, non expired access rows for a user.
 *
 * Returns `null` when the request failed, so callers can tell "we do not
 * know" apart from a confirmed empty list. A transient network drop must
 * never look like "this account lost all access".
 */
export async function fetchActiveCertAccessOrNull(
  userId: string,
  retries = 2,
): Promise<CertAccessRow[] | null> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const { data, error } = await supabase
      .from("user_certification_access")
      .select("certification_id, expires_at, source, revoked_at")
      .eq("user_id", userId)
      .is("revoked_at", null);
    if (!error && data) {
      return (data as CertAccessRow[]).filter(isAccessRowActive);
    }
    if (attempt < retries) {
      await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
    }
  }
  return null;
}

/**
 * Back compatible wrapper: an unknown result collapses to an empty list.
 * Only use this where "unknown" and "none" are safe to treat the same.
 */
export async function fetchActiveCertAccess(userId: string): Promise<CertAccessRow[]> {
  return (await fetchActiveCertAccessOrNull(userId)) ?? [];
}

/** One shared "does this account have any access at all?" check. */
export async function hasAnyActiveCertAccess(userId: string): Promise<boolean> {
  const rows = await fetchActiveCertAccessOrNull(userId);
  return (rows ?? []).length > 0;
}

